import React, { useMemo, forwardRef } from 'react';
import type { StylableButtonPlatformProps } from '@wix/editor-elements-library/dist/esm/components/StylableButton/StylableButton.types';
import ButtonSkin from '@wix/editor-elements-library/src/components/StylableButton/viewer/skinComps/Button/Button.skin';
import directionStyles from '../../../common-styles/direction.module.scss';
import presetWrapperStyles from '../../../common-styles/presetWrapper.module.scss';
import type { VectorArt, A11y, Direction } from '@wix/editor-react-types';
import AnimatedIcon from '../AnimatedIcon/component';
import type { AnimatedSvgRef } from '@wix/editor-elements-library/src/components/AnimatedSvg/AnimatedSvg.types';
import { removeAriaPrefix } from '../../../utils/a11y';
import styles from './ButtonLegacy.module.scss';
import classNames from 'classnames';

interface AnimatedIconElementProps {
  svg?: VectorArt;
  duration?: number;
}

interface ElementProps {
  animatedIcon?: AnimatedIconElementProps;
}

export type ButtonProps = Omit<Partial<StylableButtonPlatformProps>, 'a11y'> & {
  disabled?: boolean;
  direction?: Direction;
  id?: string;
  onClick?: () => void;
  wix?: {
    presetsWrapperProps?: Record<string, any>;
  };
  elementProps?: ElementProps;
  a11y?: A11y;
};

const Button: React.FC<ButtonProps> = props => {
  const presetsWrapperProps = props.wix?.presetsWrapperProps || {};

  const memoizedProps = useMemo(() => {
    const animatedIconProps = props.elementProps?.animatedIcon;

    const AnimatedIconWithProps = forwardRef<AnimatedSvgRef>((_, ref) => (
      <AnimatedIcon
        id="animated-icon"
        duration={animatedIconProps?.duration}
        svg={animatedIconProps?.svg}
        className={classNames('animated-icon', styles.icon)}
        ref={ref}
      />
    ));

    const baseProps = {
      ...props,
      type: props.type || 'button',
      svgString: '',
      label: props.label || '',
      isDisabled: props.disabled ?? props.isDisabled ?? false,
      a11y: removeAriaPrefix(props.a11y ?? {}),
      link: props.link || undefined,
      extendedStyleApi: false,
      labelClass: 'label',
      iconClass: 'icon',
      className: classNames(props.className, styles.button, 'button'),
      fallbackDirectionClassName: directionStyles.fallbackDirection,
      BuilderAnimationIcon: AnimatedIconWithProps,
    };
    return baseProps as React.ComponentProps<typeof ButtonSkin>;
  }, [props]);

  return (
    <div className={presetWrapperStyles.presetWrapper} {...presetsWrapperProps}>
      <ButtonSkin {...memoizedProps} />
    </div>
  );
};

export default Button;
